import React, { FC, useMemo } from "react";
import CategoryCardList from "./general/CategoryCardList";
import { Box, Button, H2, SafeAreaView, Text } from "./ui";
import { useGetCategoryQuery } from "store/abcoApi";
import { Tab, TabContainer } from "./general/Tabs";
import { H1 } from "@expo/html-elements";
import PageLoader from "./general/PageLoader";
import { Drill, PackageSearch, Wrench } from "lucide-react-native";
import assetManagementImage from "assets/asset management 2 2.jpg";
import mobilisationsImage from "assets/images/mobilisation.jpg";
import servicingAndMaintenanceImage from "assets/images/servicing and maintenance.jpg";
import trainingAndTechnicalSupportImage from "assets/images/training and technical support.jpg";
import deliveryImage from "assets/images/delivery.jpg";
import trainingCoursesImage from "assets/Training Courses.jpg";
import siteManagementImage from "assets/Site Management Services.jpg";
import testAndTagImage from "assets/Test and Tag.jpg";
import { useSiteRegion } from "utils/hooks/useSiteSection";

interface CategoryCardTabListProps {
	isExploreCatalogue?: boolean;
}

export const CategoryCardTabList: FC<CategoryCardTabListProps> = ({ isExploreCatalogue }) => {
	const { data, isLoading, isFetching, isError, error, currentData } = useGetCategoryQuery();

	const servicesList = [
		{
			name: "Training",
			slug: "training",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: trainingCoursesImage,
			url: "/services/training",
			seoDescription: null,
			seoTitle: null,
			description: null,
		},
		{
			name: "Asset Management",
			slug: "asset-management",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: assetManagementImage,
			url: "/services/asset-management",
			seoDescription: null,
			seoTitle: null,
			description: null,
		},
		{
			name: "Site Management Services",
			slug: "site-management-services",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: siteManagementImage,
			url: "/services/site-management-services",
			seoDescription: null,
			seoTitle: null,
			description: null,
		},
		{
			name: "Test and Tag",
			slug: "test-and-tag",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: testAndTagImage,
			url: "/services/test-and-tag",
			seoDescription: null,
			seoTitle: null,
			description: null,
		},
	];

	const servicesListAU = [
		{
			name: "Mobilisations",
			slug: "mobilisations",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: mobilisationsImage,
			url: "/services/mobilisations",
			seoDescription:
				"Abco excels in mobilising large-scale projects, handling everything from pre-site audits and equipment setup to staff training. Delays are minimised and new contracts and sites can be made fully operational and supported from day one. Mobilisation support will include tailored resources and dedicated support",
			seoTitle: null,
			description: null,
		},
		{
			name: "Servicing & Maintenance",
			slug: "servicing-and-maintenance",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: servicingAndMaintenanceImage,
			url: "/services/servicing-and-maintenance",
			seoDescription:
				"Abco offers both preventative maintenance and urgent repair services, aiming to keep equipment functional. Our preventative maintenance program reduces equipment downtime, avoids costly breakdowns and maximises lifespan and productivity. Our national network of mobile technicians and approved contractors enables quick, localised repairs, while planned maintenance keeps equipment functioning optimally. ",
			seoTitle: null,
			description: null,
		},
		{
			name: "Training & Technical Support",
			slug: "training-and-technical-support",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: trainingAndTechnicalSupportImage,
			url: "/services/training-and-technical-support",
			seoDescription:
				"At Abco, we understand that success in the commercial cleaning industry is more than just quality products. it's about empowering our partners with knowledge and support that sets them apart. Our commitment to success is reflected in our comprehensive training programs and technical support services. ",
			seoTitle: null,
			description: null,
		},
		{
			name: "Delivery",
			slug: "delivery",
			categoryType: "Services",
			subCategoryCollection: null,
			imageUrl: deliveryImage,
			url: "/services/delivery",
			seoDescription:
				"At Abco, we provide more than products—we empower your cleaning operations and streamline logistics through solutions tailored to the needs of commercial cleaners and facility managers. With efficient, reliable delivery services, we help streamline your operations, ensuring supplies are exactly where you need them, when you need them. ",
			seoTitle: null,
			description: null,
		},
	];

	const combinedData = useMemo(() => {
		if (!data) return [];
		const region = useSiteRegion();
		if (region === "au") {
			return [...data, ...servicesListAU];
		}

		return [...data, ...servicesList];
	}, [data]);

	if (isFetching || isLoading) {
		return <PageLoader />;
	}

	if (isExploreCatalogue) {
		return (
			<Box>
				<TabContainer>
					<Tab startIcon={<PackageSearch />} title="Products" id="products">
						<CategoryCardList withPagination={isExploreCatalogue} list={combinedData?.filter((item) => item.categoryType === "Products")} />
						<Box marginVertical={12} pb={24} justifyContent="center" alignItems="center">
							<Button variant="secondary" mt={32} isLink href={"/products"}>
								<Text color="black">Browse all products</Text>
							</Button>
						</Box>
					</Tab>
					<Tab startIcon={<Drill />} title="Equipment" id="equipment">
						<CategoryCardList withPagination={isExploreCatalogue} list={combinedData?.filter((item) => item.categoryType === "Equipment")} />
						<Box marginVertical={12} pb={24} justifyContent="center" alignItems="center">
							<Button variant="secondary" mt={32} isLink href={"/equipment"}>
								<Text color="black">Browse all equipment</Text>
							</Button>
						</Box>
					</Tab>
					<Tab startIcon={<Wrench />} title="Services" id="services">
						<CategoryCardList withPagination={isExploreCatalogue} list={combinedData?.filter((item) => item.categoryType === "Services")} />
						<Box marginVertical={12} pb={24} justifyContent="center" alignItems="center">
							<Button variant="secondary" mt={32} isLink href={"/services"}>
								<Text color="black">Browse all services</Text>
							</Button>
						</Box>
					</Tab>
				</TabContainer>
			</Box>
		);
	}

	return (
		<SafeAreaView h="$full">
			<TabContainer>
				<Tab title="Products" id="products">
					<CategoryCardList list={data?.filter((item) => item.categoryType === "Products")} />
				</Tab>
				<Tab title="Machinery" id="machinery">
					<CategoryCardList list={data?.filter((item) => item.categoryType === "Machinery")} />
				</Tab>
			</TabContainer>
		</SafeAreaView>
	);
};
